// ─────────────────────────────────────────────────────────────────────────────
// Extractor Studio billing — shared rules, labels and formatters.
//
// The transition table and status enum below mirror the backend exactly
// (extractor_studio/billing/validation.py `validate_transition()` and the
// account status model). Rendering a button the backend would reject is the
// main failure mode on this page, so the allowed set lives in one place.
// Colours come from ../studioTokens (primary #A04A97) — never ../../constants.
// ─────────────────────────────────────────────────────────────────────────────

export const TRIAL_PLAN_KEY = "trial";

/* The deployed catalog and the integration guide disagree on plan keys: staging
   currently serves `individual` / `professional`, the stripe-extractor branch
   documents `starter_monthly` / `professional_monthly` / `starter_annual`. Map
   the old names onto the documented ones so the transition rules, the "most
   popular" flag and the annual-saving maths work against either payload.
   Delete this once staging serves the documented keys. */
const PLAN_ALIASES = {
  individual: "starter_monthly",
  starter: "starter_monthly",
  professional: "professional_monthly",
  team: "professional_monthly",
};
export const canonicalPlanKey = (key) => PLAN_ALIASES[key] || key;

export const PLAN_STARTER = "starter_monthly";
export const PLAN_PROFESSIONAL = "professional_monthly";
export const PLAN_ANNUAL = "starter_annual";

/* Which target plans `POST change-plan/` accepts, keyed by the plan the account
   is on now. `immediate` takes effect at once (prorated / interval switch);
   `scheduled` lands at period end. Everything absent here is rejected with
   error_code "billing_validation", so we render no button for it at all. */
export const PLAN_TRANSITIONS = {
  [PLAN_STARTER]: { [PLAN_PROFESSIONAL]: "immediate", [PLAN_ANNUAL]: "immediate" },
  [PLAN_PROFESSIONAL]: { [PLAN_STARTER]: "scheduled" },
  [PLAN_ANNUAL]: { [PLAN_STARTER]: "scheduled" },
};

export const transitionKind = (fromKey, toKey) =>
  (PLAN_TRANSITIONS[canonicalPlanKey(fromKey)] || {})[canonicalPlanKey(toKey)] || null;

/* ── Catalog normalisation ──────────────────────────────────────────────────
   `plans/` is served by two backend generations. The current staging build
   returns { monthly_credits, stripe_price_id, public } and no `addons` key at
   all; the documented build returns { credits, billing_interval } plus addons.
   Normalise once here so no component has to know which one answered. */

const inferInterval = (raw) => {
  const key = String(raw.key || "");
  if (/annual|yearly/i.test(key)) return "annual";
  if (!Number(raw.price_usd)) return "lifetime";
  return "monthly";
};

export const normalizePlan = (raw) => ({
  key: raw.key,
  name: raw.name,
  price_usd: Number(raw.price_usd || 0),
  credits: Number(raw.credits ?? raw.monthly_credits ?? 0),
  billing_interval: raw.billing_interval || inferInterval(raw),
  description: raw.description || "",
});
/* Deliberately not derived here: `stripe_price_id`. The public catalog nulls
   it out on some builds even when a Price is configured server-side, so it
   can't be used to decide whether checkout will work. The backend is the
   gate — a genuinely missing Price comes back as a 503 stripe_configuration,
   which billingErrMsg already explains. */

export const normalizeAddon = (raw) => ({
  key: raw.key,
  name: raw.name,
  price_usd: Number(raw.price_usd || 0),
  credits: Number(raw.credits ?? raw.monthly_credits ?? 0),
});

/* Statuses that can still start a first paid subscription through `checkout/`.
   The backend only documents `trial`, but the status model says expired
   accounts should see plan cards again so they can re-subscribe; `cancelled`
   is a reserved enum value we treat like expired defensively. */
/* Phase 2 of the Stripe guide: only offer `checkout/` while the account is
   still on trial. The status model additionally wants expired/cancelled
   accounts to be able to re-subscribe, so those two are allowed through as
   well — every other state goes via change-plan/ or the portal. */
const RESUBSCRIBE_STATUSES = new Set(["expired", "cancelled"]);
// The old plan is over, so any paid plan can be bought, that same plan included.
export const planEnded = (balance) => RESUBSCRIBE_STATUSES.has(balance?.status);
/* A plan given by hand (Django admin) has no Stripe subscription behind it, so there's nothing on
   Stripe to change or update: every move to a paid plan goes through Checkout, which creates one. */
export const isManualPlan = (balance) =>
  balance?.has_stripe_subscription === false && balance.plan_key !== TRIAL_PLAN_KEY;
export const canStartCheckout = (balance) => {
  if (!balance) return false;
  if (balance.plan_key === TRIAL_PLAN_KEY || isManualPlan(balance)) return true;
  if (planEnded(balance)) return true;
  /* If the balance payload carries no status at all we cannot tell which
     state we're in — offer checkout and let the backend reject it. Hiding
     every button on a missing field strands the user with no way forward,
     which is strictly worse than a 402 they can read. */
  return !balance.status;
};

export const STATUS_META = {
  trial: { label: "Trial", tone: "warn" },
  active: { label: "Active", tone: "ok" },
  payment_required: { label: "Payment required", tone: "danger" },
  paused: { label: "Subscription paused", tone: "danger" },
  cancelled: { label: "Cancelled", tone: "neutral" },
  expired: { label: "Expired", tone: "danger" },
};
export const statusMeta = (s) => STATUS_META[s] || { label: s || "Unknown", tone: "neutral" };

/* Alert surfaces for the status banners — border/background/text as a set so a
   banner never mixes an amber border with a red body. */
export const ALERT = {
  danger: { wrap: "bg-[#FBE9E5] border-[#F0CFC7]", icon: "bg-white text-[#B33A1E]", text: "text-[#7A2318]" },
  warn: { wrap: "bg-[#FBF0E0] border-[#F0DCB8]", icon: "bg-white text-[#C77A1C]", text: "text-[#7C4D12]" },
  info: { wrap: "bg-[#FBF5FA] border-[#E9D2E6]", icon: "bg-white text-[#A04A97]", text: "text-[#6C2F66]" },
  neutral: { wrap: "bg-[#F5F5F5] border-[#ECE9EE]", icon: "bg-white text-[#52525B]", text: "text-[#52525B]" },
};

/* Ledger `kind` values. The empty value is the "no filter" option — the API
   omits the param entirely rather than sending kind="". */
export const TX_KINDS = [
  { value: "", label: "All activity" },
  { value: "extraction", label: "Extraction" },
  { value: "ingestion", label: "Ingestion" },
  { value: "template_build", label: "Template build" },
  { value: "refine", label: "Refine" },
  { value: "plan_grant", label: "Plan grant" },
  { value: "purchase", label: "Purchase" },
  { value: "plan_carryover", label: "Carryover" },
  { value: "adjustment", label: "Adjustment" },
  { value: "refund", label: "Refund" },
  { value: "expiry", label: "Expiry" },
  { value: "subscription", label: "Subscription" },
];
const TX_KIND_LABEL = Object.fromEntries(TX_KINDS.map((k) => [k.value, k.label]));
export const txKindLabel = (k) => TX_KIND_LABEL[k] || k;

export const INTERVAL_LABEL = { monthly: "per month", annual: "per year", lifetime: "one-time" };
export const INTERVAL_SUFFIX = { monthly: "/mo", annual: "/yr", lifetime: "" };

// ── Formatters ──────────────────────────────────────────────────────────────
export const fmtNum = (n) => Number(n || 0).toLocaleString("en-US");

/* Compact credit counts for stat tiles and meter captions (12.4k, 1.2M). */
export const fmtCredits = (n) => {
  const v = Number(n || 0);
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(abs % 1_000_000 === 0 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${sign}${abs % 1_000 === 0 ? abs / 1_000 : (abs / 1_000).toFixed(1)}k`;
  return `${sign}${abs}`;
};

export const fmtUsd = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

export const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

/* "Jul 12 → Aug 12"; the years are spelled out only when the two dates fall in different years. */
export const fmtPeriod = (start, end) => {
  if (!start && !end) return "—";
  const sameYear = start && end && new Date(start).getFullYear() === new Date(end).getFullYear();
  if (!sameYear) return `${fmtDate(start)} → ${fmtDate(end)}`;
  const short = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${short(start)} → ${short(end)}`;
};

/* Whole days from now until `iso`, floored at 0. Used for the renewal caption
   and the payment-recovery countdown. */
export const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.ceil((d.getTime() - Date.now()) / 86_400_000));
};

/* Every plan ships the same capabilities — only the credit allocation and the
   billing interval differ (per the public catalog copy). Derive the bullet
   list from the payload rather than hardcoding per-plan claims. */
export const planFeatures = (plan) => {
  const annual = plan.billing_interval === "annual";
  return [
    `${fmtNum(plan.credits)} credits ${annual ? "for the annual period" : "per month"}`,
    annual ? "No monthly cap — spend at your own pace" : "Refreshes every billing cycle · no rollover",
    "All extraction engines",
    "Full API access",
    "JSON / CSV / Excel exports",
  ];
};

/* Annual savings vs. paying the monthly plan for 12 months. Computed from the
   catalog so a price change on the backend doesn't strand a hardcoded "17%". */
export const annualSavingPct = (annualPlan, monthlyPlan) => {
  if (!annualPlan || !monthlyPlan || !monthlyPlan.price_usd) return null;
  const pct = Math.round((1 - annualPlan.price_usd / (monthlyPlan.price_usd * 12)) * 100);
  return pct > 0 ? pct : null;
};

/* Billing errors come back as { error, error_code }. Two codes are server-side
   problems whose raw text is meaningless (or misleading) to the user — the
   rest carry a message that is safe to show verbatim, which is what errMsg
   already does. */
const OPAQUE_ERROR_CODES = new Set(["stripe_configuration", "stripe_action_failed"]);

export const billingErrMsg = (err, fallback) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  /* A 404 here means the route itself is missing — the billing endpoints are
     only on the stripe-extractor backend branch. Say that plainly rather than
     dressing a deployment gap up as a transient payment failure. */
  if (status === 404)
    return "This billing action isn't available on the connected backend yet. The Stripe billing endpoints haven't been deployed to this environment.";
  if (OPAQUE_ERROR_CODES.has(data?.error_code))
    return "Payments are temporarily unavailable — Stripe isn't fully configured on the server. Please try again later.";
  return data?.error || data?.detail || fallback;
};
